import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';

import { useUiMessages } from '../../messages';
import type { UiMessages } from '../../messages';
import { useBreakpoint } from '../../tokens/breakpoints';
import { Button } from '../Button';
import { DateField } from '../DateField';
import { DropdownMenu } from '../DropdownMenu';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { MultiSelect } from '../MultiSelect';
import { Select } from '../Select';
import type { SelectOption } from '../Select';
import { Typography } from '../Typography';
import { UtilityButton } from '../UtilityButton';

import styles from './TableFilter.module.css';
import type { FilterCondition, FilterOperator } from './types';
import type { TableFilterField } from './useTableFilterFields';

/** User-facing strings. The package has no i18n dependency, so copy arrives through the provider. */
export type TableFilterLabels = UiMessages['filter'];

const DEFAULT_LABELS: TableFilterLabels = {
  open: 'Filter',
  sheetTitle: 'Filters',
  close: 'Close filters',
  add: 'Add filter',
  clear: 'Clear',
  apply: 'Apply filter',
  remove: 'Remove filter',
  emptyTitle: 'No filter applied',
  emptyText: 'Add filters to narrow down results.',
  field: 'Filter field',
  selectField: 'Select field',
  operator: 'Filter operator',
  value: 'Filter value',
  selectValue: 'Select',
  enterValue: 'Enter a value',
  valueFrom: 'From date',
  valueTo: 'To date',
  rangeFrom: 'From',
  rangeTo: 'To',
  clearDate: 'Clear date',
  selected: (count) => `${String(count)} selected`,
  operatorEquals: 'Equals',
  operatorContains: 'contains',
  operatorBetween: 'is between',
  operatorOnOrBefore: 'On or before',
  operatorGreaterThan: 'Greater than',
  operatorLessThan: 'Less than',
};

export interface TableFilterProps {
  /** The fields this table can filter on, derived from its columns. */
  fields: TableFilterField[];
  /** The applied set, from the URL. */
  conditions: FilterCondition[];
  onApply: (next: FilterCondition[]) => void;
  onClear: () => void;
  /** Overrides for individual strings; the provider supplies the rest. */
  labels?: Partial<TableFilterLabels>;
  className?: string;
}

interface DraftRow {
  key: number;
  fieldId: string;
  operator: FilterOperator;
  /**
   * One entry holding the typed text for a `text` field; one per checked option for an `enum`; the
   * lower and upper bound, in that order, for a `date`.
   */
  values: string[];
}

/** Size of the inline clear affordance a filled value control carries. */
const CLEAR_ICON_SIZE = 16;

/** The two positions a range keeps, so an empty bound stays the bound it was. */
const RANGE_FROM = 0;
const RANGE_TO = 1;

/**
 * The operators a field of this type can express, when the field itself does not narrow that
 * further. Order here is the order the operator select lists them in.
 */
const OPERATORS_BY_TYPE: Record<TableFilterField['type'], FilterOperator[]> = {
  text: ['eq', 'contains'],
  // An enumeration compares against a closed list, so a substring match has no meaning on it.
  enum: ['eq'],
  'enum-one': ['eq'],
  // The endpoints bound a date with a `…From`/`…To` pair and nothing else — there is no
  // "on this day" parameter to offer, and a range of one day expresses it anyway.
  date: ['between'],
  // A single upper bound has exactly one operator worth offering: the value control already is
  // the bound, so there is nothing an `Equals` or a range would add.
  'date-max': ['lte'],
  // A quantity is compared against, not matched: "larger than 100 MB", "between 1 and 5 MB".
  // Equality on a byte count is a question nobody asks.
  number: ['gt', 'lt', 'between'],
};

/**
 * The operators a field actually offers: its own `operators` list when it declares one, so a page
 * can narrow a type's usual set (an `enum-one` field the endpoint only ever equates against, say),
 * or the type's full set otherwise.
 */
function operatorsFor(field: TableFilterField | undefined): FilterOperator[] {
  return field?.operators ?? OPERATORS_BY_TYPE[field?.type ?? 'text'];
}

/**
 * The operator to carry into a field change, preferring the one already chosen.
 *
 * A field change must not leave the row holding an operator its own select no longer lists — the
 * row would then read as `Equals` while carrying `between`, and apply as neither. Falling back to
 * the new field's first operator keeps the row valid without guessing which one the page meant.
 */
function operatorFor(field: TableFilterField | undefined, current: FilterOperator): FilterOperator {
  const allowed = operatorsFor(field);
  return allowed.includes(current) ? current : (allowed[0] ?? current);
}

/**
 * The table filter popup (HS-1154 and its follow-up).
 *
 * A row is a field, an operator and a value, matching the Figma builder: the field select carries a
 * trailing count of the values that field offers, the operator select offers what the field can
 * express — its own `operators` list when it declares one, otherwise its type's usual set — and the
 * value control follows the field's type: a free-text input, a multi-select checkbox list with
 * `Reset` / `Select all`, a single-choice select for `enum-one`, the two bounds of a date range, or
 * the one bound `date-max` takes. Below the breakpoint `DropdownMenu` presents itself as a sheet,
 * which is the designed mobile presentation.
 *
 * **Applying narrows the rows on the server.** The page declares which of its fields map to which
 * query parameter; see `buildFilterParams` in `@/utils/listQueryParams`. A field with no mapping
 * stays presentational rather than narrowing the loaded page, which would disagree with the count.
 *
 * **The panel is a `dialog`, not a `menu` or a `listbox`.** Those two roles are composite widgets
 * whose children must be `menuitem`s or `option`s, so `aria-required-children` fails the moment a
 * combobox goes inside one. The popup holds a form, so `dialog` is both correct and what the
 * trigger's `aria-haspopup` already claimed.
 */
export function TableFilter({
  fields,
  conditions,
  onApply,
  onClear,
  labels,
  className,
}: TableFilterProps) {
  const messages = useUiMessages();
  const t = { ...DEFAULT_LABELS, ...messages.filter, ...labels };
  // The same signal `DropdownMenu` uses to choose the sheet presentation, so the header this adds
  // for the sheet cannot appear on a desktop popup because two width tests disagreed.
  const isMobile = useBreakpoint() === 'sm';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<DraftRow[]>([]);
  // Row keys have to be stable across re-renders and unique across adds; a counter is both, and
  // unlike `Math.random()` it does not change what the component renders between two identical
  // states.
  const nextKey = useRef(0);

  const fieldById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);

  const fieldOptions = useMemo<SelectOption[]>(
    () =>
      fields.map((field) => ({
        value: field.id,
        label: field.label,
        // The design's trailing count is the size of the field's vocabulary. A free-text field has
        // no vocabulary, so it carries no count rather than a misleading zero. `enum-one` has a
        // vocabulary just as `enum` does — the two differ only in how many of it a row may pick.
        supportingText:
          field.type === 'enum' || field.type === 'enum-one'
            ? String(field.options.length)
            : undefined,
      })),
    [fields],
  );

  const operatorLabels: Record<FilterOperator, string> = {
    eq: t.operatorEquals,
    contains: t.operatorContains,
    between: t.operatorBetween,
    lte: t.operatorOnOrBefore,
    gt: t.operatorGreaterThan,
    lt: t.operatorLessThan,
  };

  const handleToggle = () => {
    // Opening seeds the draft from what is applied, so the popup always shows the current state and
    // an abandoned edit does not survive the next open.
    if (!open) {
      setRows(
        conditions.map((condition) => {
          nextKey.current += 1;
          return {
            key: nextKey.current,
            fieldId: condition.field,
            operator: condition.operator,
            values: condition.values,
          };
        }),
      );
    }
    setOpen((v) => !v);
  };

  const addRow = () => {
    nextKey.current += 1;
    setRows((current) => [
      ...current,
      { key: nextKey.current, fieldId: '', operator: 'eq', values: [] },
    ]);
  };

  const updateRow = (key: number, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const removeRow = (key: number) => {
    setRows((current) => current.filter((row) => row.key !== key));
  };

  const changeField = (row: DraftRow, fieldId: string) => {
    const field = fieldById.get(fieldId);
    updateRow(row.key, {
      fieldId,
      // The value belongs to the old field's vocabulary, so it cannot survive the change.
      values: [],
      // Keep the operator inside what the new field's own select offers, rather than leaving a
      // selection it no longer lists: `contains` does not exist for an enumeration, `between`
      // exists only for a date, and a field with its own `operators` may offer fewer still.
      operator: operatorFor(field, row.operator),
    });
  };

  const handleApply = () => {
    onApply(rows.map(({ fieldId, operator, values }) => ({ field: fieldId, operator, values })));
    setOpen(false);
  };

  const handleClear = () => {
    setRows([]);
    onClear();
    setOpen(false);
  };

  const close = () => {
    setOpen(false);
  };

  return (
    <div className={classNames(styles.root, className)}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t.open}
        onClick={handleToggle}
      >
        <Icon name="filter-funnel-01" size={20} />
        {/* The applied state is the dot alone — the design shows no chip row and no other summary. */}
        {conditions.length > 0 && <span className={styles.dot} aria-hidden="true" />}
      </button>

      <DropdownMenu
        anchorRef={triggerRef}
        open={open}
        onClose={close}
        role="dialog"
        aria-label={t.sheetTitle}
        // The design hangs the popup's right edge off the funnel and opens it leftward over the
        // table, rather than starting at the trigger and running off towards the primary action.
        placement="bottom-end"
        matchWidth={false}
        className={styles.popup ?? ''}
        // The design gives only the sheet a header. The desktop popup opens straight onto its rows.
        header={
          isMobile ? (
            <div className={styles['sheet-head']}>
              <Icon name="filter-funnel-01" size={20} />
              <div className={styles['sheet-copy']}>
                <Typography as="h2" variant="text-sm" weight="semibold">
                  {t.sheetTitle}
                </Typography>
                <Typography as="p" variant="text-sm" className={styles['sheet-text'] ?? ''}>
                  {t.emptyText}
                </Typography>
              </div>
              <button type="button" className={styles.remove} aria-label={t.close} onClick={close}>
                <Icon name="x-close" size={20} />
              </button>
            </div>
          ) : undefined
        }
        footer={
          <div className={styles.footer}>
            <Button
              hierarchy="tertiary"
              size="sm"
              iconLeading={<Icon name="plus" />}
              onClick={addRow}
            >
              {t.add}
            </Button>
            {rows.length > 0 && (
              <div className={styles['footer-right']}>
                <Button hierarchy="secondary" size="sm" onClick={handleClear}>
                  {t.clear}
                </Button>
                <Button hierarchy="primary" size="sm" onClick={handleApply}>
                  {t.apply}
                </Button>
              </div>
            )}
          </div>
        }
      >
        {rows.length === 0 ? (
          <div className={styles.empty}>
            <Typography
              as="p"
              variant="text-sm"
              weight="semibold"
              className={styles['empty-title'] ?? ''}
            >
              {t.emptyTitle}
            </Typography>
            <Typography as="p" variant="text-sm" className={styles['empty-text'] ?? ''}>
              {t.emptyText}
            </Typography>
          </div>
        ) : (
          <div className={styles.rows}>
            {rows.map((row) => {
              const field = fieldById.get(row.fieldId);
              // With no field chosen yet the design still shows the value input, so an unresolved
              // row falls back to free text rather than rendering nothing.
              const type = field?.type ?? 'text';
              /**
               * `between` needs two controls whatever the field is made of.
               *
               * Choosing the value control from the field's type alone was safe only while
               * `between` existed on `date` and nowhere else. A field may now declare its own
               * operator list, so a `text` field offering `between` would otherwise render one
               * input, take one value, and have it padded into a half-open range the user never
               * asked for (HS-1440 review). The operator decides how many slots; the type decides
               * what each slot is.
               *
               * An enumeration is the exception: `between` over a checkbox list has no meaning,
               * so it keeps its single control and the operator is simply not offered for it.
               */
              const isRange = row.operator === 'between' && type !== 'enum';
              const setValue = (index: number, value: string) => {
                const values = isRange
                  ? [row.values[RANGE_FROM] ?? '', row.values[RANGE_TO] ?? '']
                  : [...row.values];
                values[index] = value;
                updateRow(row.key, { values });
              };

              const slot = (index: number, label: string) => {
                const value = row.values[index] ?? '';
                // A field's own example beats the generic prompt: "e.g. 09:00" says what to type.
                const upper = index === RANGE_TO ? field?.upperPlaceholder : undefined;
                const placeholder = upper ?? field?.placeholder ?? t.enterValue;
                const clear =
                  value === '' ? undefined : (
                    <UtilityButton
                      size="xs"
                      hierarchy="tertiary"
                      icon={<Icon name="x-close" size={CLEAR_ICON_SIZE} />}
                      label={t.clearDate}
                      onClick={() => {
                        setValue(index, '');
                      }}
                    />
                  );

                if (type === 'date') {
                  return (
                    <Input
                      label={label}
                      hideLabel
                      type="date"
                      value={value}
                      onChange={(event) => {
                        setValue(index, event.target.value);
                      }}
                      size="sm"
                      trailingIcon={clear}
                    />
                  );
                }
                if (type === 'number') {
                  return (
                    <Input
                      label={label}
                      hideLabel
                      type="number"
                      inputMode="decimal"
                      min={0}
                      value={value}
                      onChange={(event) => {
                        setValue(index, event.target.value);
                      }}
                      placeholder={placeholder}
                      size="sm"
                    />
                  );
                }
                if (type === 'date-max') {
                  return (
                    <DateField
                      label={label}
                      hideLabel
                      value={value}
                      onChange={(next) => {
                        setValue(index, next);
                      }}
                      size="sm"
                      clearLabel={t.clearDate}
                    />
                  );
                }
                if (type === 'enum-one') {
                  return (
                    <Select
                      label={label}
                      hideLabel
                      options={field?.options ?? []}
                      value={value}
                      onChange={(next) => {
                        setValue(index, next);
                      }}
                      placeholder={t.selectValue}
                      size="sm"
                    />
                  );
                }
                return (
                  <Input
                    label={label}
                    hideLabel
                    value={value}
                    onChange={(event) => {
                      setValue(index, event.target.value);
                    }}
                    placeholder={placeholder}
                    size="sm"
                  />
                );
              };

              return (
                <div
                  key={row.key}
                  className={classNames(styles.row, isRange && styles['row-range'])}
                >
                  <Select
                    label={t.field}
                    hideLabel
                    options={fieldOptions}
                    value={row.fieldId}
                    onChange={(value) => {
                      changeField(row, value);
                    }}
                    placeholder={t.selectField}
                    supportingTextInMenuOnly
                    size="sm"
                  />
                  <Select
                    label={t.operator}
                    hideLabel
                    options={operatorsFor(field).map((operator) => ({
                      value: operator,
                      label: operatorLabels[operator],
                    }))}
                    value={row.operator}
                    onChange={(value) => {
                      updateRow(row.key, { operator: value as FilterOperator });
                    }}
                    size="sm"
                  />
                  {isRange ? (
                    <div className={styles.range}>
                      {slot(RANGE_FROM, type === 'date' ? t.valueFrom : t.rangeFrom)}
                      {slot(RANGE_TO, type === 'date' ? t.valueTo : t.rangeTo)}
                    </div>
                  ) : type === 'enum' ? (
                    <MultiSelect
                      label={t.value}
                      hideLabel
                      options={field?.options ?? []}
                      value={row.values}
                      onChange={(values) => {
                        updateRow(row.key, { values });
                      }}
                      placeholder={t.selectValue}
                      summarizeSelection={(count) => t.selected(count)}
                      showFooter
                      size="sm"
                    />
                  ) : (
                    slot(RANGE_FROM, t.value)
                  )}
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={t.remove}
                    onClick={() => {
                      removeRow(row.key);
                    }}
                  >
                    <Icon name="x-close" size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </DropdownMenu>
    </div>
  );
}

export default TableFilter;
